import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';

export const RESOURCE_TYPES = [
  'githubTeam',
  'githubRepository',
  'githubApp',
] as const;

export type ResourceType = (typeof RESOURCE_TYPES)[number];
export type RepositoryVisibility = 'PRIVATE' | 'INTERNAL';

export const normalizeRepositoryVisibility = (
  value: string | null | undefined,
): RepositoryVisibility | undefined => {
  const normalized = value?.trim().toUpperCase();
  return normalized === 'PRIVATE' || normalized === 'INTERNAL'
    ? normalized
    : undefined;
};

export type RecertificationResult = 'KEEP' | 'DELETE';

export interface RecertificationSubmission {
  result: RecertificationResult;
  apmId?: string;
  digiPiId?: string;
  visibility?: RepositoryVisibility;
  businessJustification?: string;
  attestations: {
    stillNeeded: boolean;
    permissionsReviewed: boolean;
    takeResponsibility: boolean;
    repositoryContentReviewed?: boolean;
  };
}

export interface RecertificationRow {
  id: string;
  resourceId: string;
  displayName: string;
  lastRecertifiedOn: string | null;
  lastRecertifiedBy: string | null;
  owners: string[];
  htmlUrl: string | null;
  apmId?: string | null;
  visibility?: string | null;
}

export interface RecertificationLogEntry {
  recertifiedOn: string | null;
  recertifiedBy: string | null;
}

export interface RecertificationApi {
  listMine(type: ResourceType): Promise<RecertificationRow[]>;
  getMine(type: ResourceType, resourceId: string): Promise<RecertificationRow>;
  recertify(
    type: ResourceType,
    resourceId: string,
    submission: RecertificationSubmission,
  ): Promise<RecertificationRow>;
  history(
    type: ResourceType,
    resourceId: string,
  ): Promise<RecertificationLogEntry[]>;
}

export const recertificationApiRef = createApiRef<RecertificationApi>({
  id: 'plugin.recertification.service',
});

export class RecertificationClient implements RecertificationApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('recertification');
  }

  async listMine(type: ResourceType): Promise<RecertificationRow[]> {
    const base = await this.baseUrl();
    const res = await this.fetchApi.fetch(
      `${base}/mine?type=${encodeURIComponent(type)}`,
    );
    if (!res.ok) throw await ResponseError.fromResponse(res);
    return (await res.json()) as RecertificationRow[];
  }

  async getMine(
    type: ResourceType,
    resourceId: string,
  ): Promise<RecertificationRow> {
    const base = await this.baseUrl();
    const res = await this.fetchApi.fetch(
      `${base}/${encodeURIComponent(type)}/${encodeURIComponent(resourceId)}`,
    );
    if (!res.ok) throw await ResponseError.fromResponse(res);
    return (await res.json()) as RecertificationRow;
  }

  async recertify(
    type: ResourceType,
    resourceId: string,
    submission: RecertificationSubmission,
  ): Promise<RecertificationRow> {
    const base = await this.baseUrl();
    const res = await this.fetchApi.fetch(
      `${base}/${encodeURIComponent(type)}/${encodeURIComponent(resourceId)}/recertify`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submission),
      },
    );
    if (!res.ok) throw await ResponseError.fromResponse(res);
    return (await res.json()) as RecertificationRow;
  }

  async history(
    type: ResourceType,
    resourceId: string,
  ): Promise<RecertificationLogEntry[]> {
    const base = await this.baseUrl();
    const res = await this.fetchApi.fetch(
      `${base}/${encodeURIComponent(type)}/${encodeURIComponent(resourceId)}/history`,
    );
    if (!res.ok) throw await ResponseError.fromResponse(res);
    return (await res.json()) as RecertificationLogEntry[];
  }
}
